import { Autocomplete, Stack, TextField as MuiTextField, Tooltip, Typography } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';

import { useFieldArray, useFormContext } from 'FrontRoyalReactHookForm';
import { TextField } from 'FrontRoyalMaterialUiForm';
import ComingSoonToggle from './ComingSoonToggle';
import ItemPanel from './ItemPanel';
import { useSortableList } from './useSortableList';
import { type EmbeddedLesson, type StreamFormValues } from './StreamEditor.types';
import {
    OpenInNewTabButton,
    RemoveButton,
    ReorderButtons,
    renderOptionKeyedById,
    titleWithTag,
} from 'ContentItemEditor';

type Props = {
    index: number;
    chapterCount: number;
    lessonsById: Record<string, EmbeddedLesson>;

    // The lessons that may still be added. The caller leaves out the ones already in the course.
    lessonOptions: EmbeddedLesson[];
    lessonsLoaded: boolean;

    // True once assessment editing is locked for this course. An assessment may then not be added
    // or removed, although an ordinary lesson still may.
    assessmentEditingLocked: boolean;
    editorUrlForLesson: (lessonId: string) => string;
    onMoveChapter: (from: number, to: number) => void;
    onRemoveChapter: () => void;
};

function lessonTitleWithTag(lesson: EmbeddedLesson | undefined, lessonId: string) {
    return lesson ? titleWithTag(lesson) : lessonId;
}

// oxlint-disable-next-line max-lines-per-function -- one chapter with its lesson list
export default function ChapterRow({
    index,
    chapterCount,
    lessonsById,
    lessonOptions,
    lessonsLoaded,
    assessmentEditingLocked,
    editorUrlForLesson,
    onMoveChapter,
    onRemoveChapter,
}: Props) {
    const { control, watch, setFieldValue } = useFormContext<StreamFormValues>();
    const {
        fields: lessonHashes,
        append,
        remove,
        move,
    } = useFieldArray({ control, name: `chapters.${index}.lesson_hashes` });

    // Drag reorders the same list the arrows do.
    const lessonListRef = useSortableList<HTMLDivElement>(move);

    const chapter = watch(`chapters.${index}`);
    const pending = !!chapter?.pending;

    // A chapter cannot go while it holds an assessment and assessment editing is locked.
    const chapterLocked =
        assessmentEditingLocked && (chapter?.lesson_hashes || []).some(hash => lessonsById[hash.lesson_id]?.assessment);

    function lessonLocked(lessonId: string) {
        return assessmentEditingLocked && !!lessonsById[lessonId]?.assessment;
    }

    return (
        <ItemPanel state={pending ? 'coming-soon' : 'available'}>
            {/* The chapter's own actions sit in its header. Below the lesson list they read as
                though they applied to the lessons. */}
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h6">{`${index + 1}.`}</Typography>

                <TextField name={`chapters.${index}.title`} placeholder="Chapter Name" fullWidth />

                {/* One flush cluster. The reorder arrows already sit against each other, so a gap
                    before the others made them read as a separate group. */}
                <Stack direction="row" sx={{ alignItems: 'center' }}>
                    <ReorderButtons index={index} count={chapterCount} label="this chapter" onMove={onMoveChapter} />

                    <ComingSoonToggle
                        comingSoon={pending}
                        itemName={`Chapter ${index + 1}`}
                        onChange={value => setFieldValue(`chapters.${index}.pending`, value)}
                    />

                    <RemoveButton
                        name="remove_chapter"
                        label="Delete this chapter"
                        disabled={chapterLocked}
                        onClick={onRemoveChapter}
                    />
                </Stack>
            </Stack>

            <Autocomplete
                // Choosing a lesson appends it and clears the input.
                value={null}
                // Padding, not margin — see EditorCard. Matches the space above the title input.
                sx={{ paddingTop: 1 }}
                blurOnSelect
                disabled={!lessonsLoaded}
                options={lessonOptions}
                getOptionLabel={lesson => lessonTitleWithTag(lesson, lesson.id)}
                isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
                onChange={(_event, lesson) => lesson && append({ lesson_id: lesson.id, coming_soon: false })}
                renderOption={renderOptionKeyedById(lesson => lessonTitleWithTag(lesson, lesson.id))}
                renderInput={props => (
                    <MuiTextField
                        {...props}
                        name="add_lesson"
                        label={lessonsLoaded ? 'Add lesson' : 'Loading lessons...'}
                    />
                )}
            />

            {/* ref on the list, so SortableJS sees the rows as its direct children. */}
            <Stack spacing={0} ref={lessonListRef} data-sortable-lessons>
                {lessonHashes.map((hash, lessonIndex) => {
                    const lessonId = chapter?.lesson_hashes?.[lessonIndex]?.lesson_id || '';
                    const comingSoon = !!chapter?.lesson_hashes?.[lessonIndex]?.coming_soon;
                    const lessonTitle = lessonTitleWithTag(lessonsById[lessonId], lessonId);

                    return (
                        <Stack key={hash.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Tooltip title="Drag to reorder">
                                <DragIndicator
                                    data-drag-handle
                                    fontSize="small"
                                    sx={{ cursor: 'grab', color: 'action.active', flexShrink: 0 }}
                                />
                            </Tooltip>

                            {/* One row per lesson: a title too long for the column truncates into
                                its tooltip, as the course list in the group editor does. */}
                            <Typography
                                noWrap
                                sx={{ flexGrow: 1, minWidth: 0, opacity: comingSoon ? 0.25 : 1 }}
                                title={comingSoon ? `${lessonTitle} (coming soon)` : lessonTitle}
                            >
                                {lessonTitle}
                            </Typography>

                            <Stack direction="row" sx={{ alignItems: 'center' }}>
                                <OpenInNewTabButton
                                    href={editorUrlForLesson(lessonId)}
                                    label="Open this lesson in the editor"
                                />

                                <ReorderButtons
                                    index={lessonIndex}
                                    count={lessonHashes.length}
                                    label="this lesson"
                                    onMove={move}
                                />

                                <ComingSoonToggle
                                    comingSoon={comingSoon}
                                    itemName={lessonTitle}
                                    onChange={value =>
                                        setFieldValue(
                                            `chapters.${index}.lesson_hashes.${lessonIndex}.coming_soon`,
                                            value,
                                        )
                                    }
                                />

                                <RemoveButton
                                    label="Remove this lesson"
                                    disabled={lessonLocked(lessonId)}
                                    onClick={() => remove(lessonIndex)}
                                />
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>
        </ItemPanel>
    );
}
